import classNames from 'classnames';
import { capitalize } from 'lodash';
import { FC } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { BadgeIllustration } from 'src/general/components/BadgeIllustration/BadgeIllustration';
import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { Button } from 'src/general/components/Button/Button';
import { useModal } from 'src/hooks/useModal';
import { getMicrocopyBulk } from 'src/stores/microcopyStore';

import { WarrantyInfoModal } from '../WarrantyInfoModal/WarrantyInfoModal';
import styles from './WarrantyInfo.module.scss';

/**
 * TODO - Hardcoded as a temporary solution.
 * We will revisit how extended manufacturer warranty status is determined when the feature is more well-defined.
 */
const makesWithExtendedManufacturerWarranty = ['Toyota', 'Mitsubishi'];

interface WarrantyInfoProps {
  remainingWarranty?: {
    warrantyExpiryKms?: string;
    remainingMonths?: number;
    remainingKilometres?: number;
    unlimitedKilometresFlag?: boolean;
  };
  make: string;
  showWarranty: boolean;
  showEligible: boolean;
}

const monthsToYearsAndMonths = (remainingMonths: number) => {
  if (remainingMonths <= 12) {
    return `${remainingMonths} months`;
  }
  return `${Math.floor(remainingMonths / 12)} years and ${remainingMonths % 12} months`;
};

export const WarrantyInfo: FC<WarrantyInfoProps> = ({ remainingWarranty, make, showWarranty, showEligible }) => {
  const { isVisible, open, close } = useModal();
  const remainingMonths = remainingWarranty?.remainingMonths;
  const isOnlyEligible = showEligible && !showWarranty;

  let remainingYearsAndMonths = '';
  if (typeof remainingMonths !== 'undefined') {
    remainingYearsAndMonths = monthsToYearsAndMonths(remainingMonths);
  }
  const warrantyKms = `${remainingWarranty?.remainingKilometres?.toLocaleString()} km`;
  const unlimitedKilometresFlag = !!remainingWarranty?.unlimitedKilometresFlag;
  const formattedMake = capitalize(make);
  const isNotOthersBrand = makesWithExtendedManufacturerWarranty.includes(formattedMake);

  const {
    title,
    otherBrandsTitle,
    infoText,
    infoTextUnlimitedKms,
    manufacturerWarranty,
    remainingWarrantyEligibleContent,
    onlyEligibleContent,
    modalButtonLinkLabel,
    modalButtonLink,
  } = getMicrocopyBulk({
    title: [MICROCOPY.PDP_WARRANTY_TITLE],
    otherBrandsTitle: [MICROCOPY.PDP_WARRANTY_OTHER_BRANDS_TITLE],
    infoText: [MICROCOPY.PDP_WARRANTY_INFORMATION, false, { remainingYearsAndMonths, warrantyKms }],
    infoTextUnlimitedKms: [MICROCOPY.PDP_WARRANTY_INFORMATION_UNLIMITED_KILOMETRES, false, { remainingYearsAndMonths }],
    manufacturerWarranty: [MICROCOPY.PDP_WARRANTY_EXTENDED_MANUFACTURER, false, { make: formattedMake }],
    remainingWarrantyEligibleContent: [MICROCOPY.PDP_REMAINING_WARRANTY_ELIGIBLE_CONTENT],
    onlyEligibleContent: [MICROCOPY.PDP_ONLY_ELIGIBLE_CONTENT],
    modalButtonLinkLabel: [MICROCOPY.PDP_WARRANTY_MODAL_LINK_LABEL],
    modalButtonLink: [MICROCOPY.PDP_WARRANTY_MODAL_LINK],
  });

  return (
    <>
      <div className={classNames(styles.wrapper)}>
        {!isOnlyEligible && (
          <div className={styles.container}>
            <div className={styles.imgContainer}>
              <BadgeIllustration icon={BADGE.WARRANTY} background="gradient" shape="circle" className={styles.icon} />
            </div>
            <div className={styles.content}>
              <h6 className={classNames(styles.title, 'c-fs-caption', 'c-fw-bold')}>
                {isNotOthersBrand ? title : otherBrandsTitle}
              </h6>
              <p className={classNames(styles.description, 'c-fs-caption')}>
                {unlimitedKilometresFlag ? infoTextUnlimitedKms : infoText}
              </p>
            </div>
          </div>
        )}

        <div
          className={classNames(styles.eligibleContainer, {
            [styles.onlyEligibleContainer]: isOnlyEligible,
          })}
        >
          <div className={styles.imgContainer}>
            <BadgeIllustration
              icon={BADGE.EXTENDED_COVERAGE_WARRANTY}
              background="none"
              shape="square"
              className={styles.logo}
            />
          </div>
          <div
            className={classNames(styles.eligibleContent, {
              [styles.onlyEligibleContent]: isOnlyEligible,
            })}
          >
            <span className={classNames(styles.text, 'c-fs-caption')}>
              {isOnlyEligible ? onlyEligibleContent : remainingWarrantyEligibleContent}
            </span>
            &ensp;
            <Button variant="link" size="small" onClick={open} className={styles.modalLink}>
              {modalButtonLinkLabel}
            </Button>
          </div>
        </div>
      </div>
      {isNotOthersBrand && !isOnlyEligible && <p className={styles.manufacturerWarranty}>{manufacturerWarranty}</p>}
      <WarrantyInfoModal isOpen={isVisible} buttonLink={modalButtonLink} close={close} />
    </>
  );
};
